/**
 * Superadmin: CERT-In subscriber register (direction (v), 28 Apr 2022).
 *
 * One record per hiring customer (team): validated name, period of hire,
 * hostnames / IP allocation, onboarding email + IP + time, purpose, validated
 * address + phone, ownership pattern. Records survive account deletion and
 * are kept 5 years after closure. Backend: routes/admin/subscriber-register.ts
 * and lib/legal/subscriber-register.ts (CLAUDE.md §12t).
 *
 * Follows the §12 admin standard: TanStack Query reads (stale-while-
 * revalidate through <DataState>), versioned writes with a 409 banner that
 * keeps the admin's edits and offers Overwrite / Discard.
 */
import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Download, RefreshCw, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { apiErrorMessage, conflictCurrentVersion, isVersionConflict } from '@/lib/conflict';
import { DataState } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// --- Types (mirror lib/legal/subscriber-register.ts presentRecord) ---------

type FieldStatus = 'missing' | 'self_declared' | 'gateway_provided' | 'verified' | 'rejected';

interface IdentityFieldView {
  value: string | null;
  source: string | null;
  status: FieldStatus;
  validation: { status: string; method: string; evidence: string; by: string; at: string } | null;
}

interface RegisterRecord {
  teamId: string;
  status: 'open' | 'closed';
  team: { name: string | null; country: string | null; createdAt: string | null };
  owner: {
    userId: string | null;
    name: string | null;
    email: string | null;
    emailVerified: boolean;
    accountDeletedAt?: string;
  };
  ownerHistory: Array<{ name: string | null; email: string | null; until: string }>;
  signup: {
    at: string | null;
    email: string | null;
    ip: string | null;
    userAgent: string | null;
    method: string;
    emailVerified: boolean | null;
    capture: 'live' | 'backfill';
  };
  legalName: IdentityFieldView;
  address: IdentityFieldView;
  phone: IdentityFieldView;
  ownershipPattern: { value: string; by: string; at: string } | null;
  purpose: {
    value: { category: string; description: string | null };
    by: string;
    at: string;
  } | null;
  billing: {
    gateway: string;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    taxIds?: string[];
  } | null;
  period: {
    startedAt: string | null;
    endedAt: string | null;
    firstPaidAt: string | null;
    paid: Array<{
      plan: string;
      gateway: string;
      startedAt: string | null;
      endedAt: string | null;
    }>;
  };
  network: {
    note: string;
    hostnames: Array<{
      hostname: string;
      kind: string;
      addedAt: string | null;
      removedAt: string | null;
    }>;
  };
  closedAt: string | null;
  closedReason: string | null;
  retainUntil: string | null;
  gaps: string[];
  version: number;
  createdAt: string;
}

interface ListResponse {
  rows: RegisterRecord[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

const OWNERSHIP_PATTERNS: Array<[string, string]> = [
  ['individual', 'Individual'],
  ['sole_proprietorship', 'Sole proprietorship'],
  ['partnership', 'Partnership'],
  ['llp', 'LLP'],
  ['private_company', 'Private company'],
  ['public_company', 'Public company'],
  ['trust_or_society', 'Trust / society'],
  ['government', 'Government'],
  ['other', 'Other'],
];

const PURPOSES: Array<[string, string]> = [
  ['business_website', 'Business website'],
  ['online_store', 'Online store'],
  ['portfolio_or_personal', 'Portfolio / personal'],
  ['blog_or_media', 'Blog / media'],
  ['nonprofit_or_community', 'Non-profit / community'],
  ['government_or_public', 'Government / public body'],
  ['other', 'Other'],
];

const VALIDATION_METHODS: Array<[string, string]> = [
  ['gateway_record', 'Billing gateway record'],
  ['document', 'Document'],
  ['phone_otp', 'Phone OTP'],
  ['video_call', 'Video call'],
  ['other', 'Other'],
];

const GAP_LABELS: Record<string, string> = {
  legal_name: 'name',
  address: 'address',
  phone: 'phone',
  ownership_pattern: 'ownership',
  purpose: 'purpose',
  signup_ip: 'signup IP',
};

const fmtDate = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtDateTime = (d: string | null | undefined) => (d ? new Date(d).toLocaleString() : '—');

function StatusBadge({ status }: { status: FieldStatus }) {
  const styles: Record<FieldStatus, string> = {
    missing: 'border-red-200 bg-red-50 text-red-700',
    self_declared: 'border-slate-200 bg-slate-50 text-slate-700',
    gateway_provided: 'border-sky-200 bg-sky-50 text-sky-700',
    verified: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    rejected: 'border-amber-200 bg-amber-50 text-amber-800',
  };
  return (
    <Badge variant="outline" className={styles[status]}>
      {status.replace('_', ' ')}
    </Badge>
  );
}

// --- Page -------------------------------------------------------------------

export default function SubscriberRegisterPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'open' | 'closed'>('all');
  const [incomplete, setIncomplete] = useState(false);
  const [page, setPage] = useState(1);
  const [openTeamId, setOpenTeamId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const filters = { q, status, incomplete, page };
  const params = {
    page,
    limit: 50,
    status,
    ...(q ? { q } : {}),
    ...(incomplete ? { incomplete: '1' } : {}),
  };

  const listQuery = useQuery({
    queryKey: qk.subscriberRegisterList(filters),
    queryFn: async (): Promise<ListResponse> => {
      const res = await api.get('/admin/subscriber-register', { params });
      return { rows: res.data.data ?? [], meta: res.data.meta };
    },
    placeholderData: keepPreviousData,
  });

  const backfill = useMutation({
    mutationFn: async () => (await api.post('/admin/subscriber-register/backfill')).data.data,
    onSuccess: (stats: { created: number; complete: boolean; errors: number }) => {
      toast.success(
        `Backfill: ${stats.created} record(s) created${stats.complete ? '' : ' — run again to continue'}${
          stats.errors ? `, ${stats.errors} error(s)` : ''
        }`
      );
      queryClient.invalidateQueries({ queryKey: qk.subscriberRegister() });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Backfill failed')),
  });

  async function exportCsv() {
    setExporting(true);
    try {
      const { page: _page, limit: _limit, ...exportParams } = params;
      const res = await api.get('/admin/subscriber-register/export.csv', {
        params: exportParams,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tyashin-subscriber-register-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      if (res.headers?.['x-register-truncated'] === '1') {
        toast.warning('Export hit the row cap — narrow the filters and export again.');
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Subscriber register</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            CERT-In direction (v): one record per customer, kept 5 years after they leave. Every
            view and export is audit-logged.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => backfill.mutate()}
            disabled={backfill.isPending}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Backfill missing
          </Button>
          <Button size="sm" onClick={exportCsv} disabled={exporting}>
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-2.5 h-4 w-4" />
          <Input
            className="pl-9"
            placeholder="Email, hostname, name, signup IP or team id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="border-input bg-background h-9 rounded-md border px-3 text-sm"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            setPage(1);
          }}
        >
          <option value="all">All records</option>
          <option value="open">Open</option>
          <option value="closed">Closed (retained)</option>
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={incomplete}
            onChange={(e) => {
              setIncomplete(e.target.checked);
              setPage(1);
            }}
          />
          Incomplete only
        </label>
      </div>

      <DataState query={listQuery}>
        {({ rows, meta }) => (
          <div className="space-y-3">
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Subscriber</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Signup</th>
                    <th className="px-3 py-2 font-medium">Hire period</th>
                    <th className="px-3 py-2 font-medium">Missing</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted-foreground px-3 py-8 text-center">
                        No records match.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr
                      key={r.teamId}
                      className="hover:bg-muted/40 cursor-pointer border-t"
                      onClick={() => setOpenTeamId(r.teamId)}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.team.name ?? r.teamId}</div>
                        <div className="text-muted-foreground text-xs">{r.owner.email ?? '—'}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div>{r.legalName.value ?? '—'}</div>
                        <StatusBadge status={r.legalName.status} />
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{fmtDate(r.signup.at)}</div>
                        <div className="text-muted-foreground">
                          {r.signup.ip ??
                            (r.signup.capture === 'backfill' ? 'IP not captured' : '—')}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {fmtDate(r.period.startedAt)} →{' '}
                        {r.period.endedAt ? fmtDate(r.period.endedAt) : 'now'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.gaps.length === 0 ? (
                          <span className="text-emerald-700">complete</span>
                        ) : (
                          r.gaps.map((g) => GAP_LABELS[g] ?? g).join(', ')
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={r.status === 'open' ? 'secondary' : 'outline'}>
                          {r.status === 'open'
                            ? 'open'
                            : `closed · keep to ${fmtDate(r.retainUntil)}`}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {meta.total} record{meta.total === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span>
                  {meta.page} / {meta.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </DataState>

      <Dialog open={Boolean(openTeamId)} onOpenChange={(open) => !open && setOpenTeamId(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          {openTeamId && <RecordDetail teamId={openTeamId} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Detail + edit ------------------------------------------------------------

type Verdict = '' | 'verified' | 'rejected' | 'clear';
interface FieldDraft {
  value: string;
  verdict: Verdict;
  method: string;
  evidence: string;
}
interface Draft {
  legalName: FieldDraft;
  address: FieldDraft;
  phone: FieldDraft;
  ownershipPattern: string;
  purposeCategory: string;
  purposeDescription: string;
}

const IDENTITY: Array<['legalName' | 'address' | 'phone', string]> = [
  ['legalName', 'Legal name'],
  ['address', 'Postal address'],
  ['phone', 'Contact phone'],
];

function draftFrom(r: RegisterRecord): Draft {
  const field = (f: IdentityFieldView): FieldDraft => ({
    value: f.value ?? '',
    verdict: '',
    method: 'document',
    evidence: '',
  });
  return {
    legalName: field(r.legalName),
    address: field(r.address),
    phone: field(r.phone),
    ownershipPattern: r.ownershipPattern?.value ?? '',
    purposeCategory: r.purpose?.value.category ?? '',
    purposeDescription: r.purpose?.value.description ?? '',
  };
}

function RecordDetail({ teamId }: { teamId: string }) {
  const queryClient = useQueryClient();
  const recordQuery = useQuery({
    queryKey: qk.subscriberRegisterRecord(teamId),
    queryFn: async (): Promise<RegisterRecord> =>
      (await api.get(`/admin/subscriber-register/${teamId}`)).data.data,
  });
  const record = recordQuery.data;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState<{ serverVersion: number } | null>(null);

  // Hydrate the form from the server, but never over the admin's unsaved edits.
  useEffect(() => {
    if (record && !dirty) setDraft(draftFrom(record));
  }, [record, dirty]);

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => (d ? { ...d, ...patch } : d));
    setDirty(true);
  };
  const updateField = (f: 'legalName' | 'address' | 'phone', patch: Partial<FieldDraft>) => {
    setDraft((d) => (d ? { ...d, [f]: { ...d[f], ...patch } } : d));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: async (opts: { force?: boolean } = {}) => {
      if (!draft || !record) throw new Error('nothing to save');
      const body: Record<string, unknown> = {};
      const validation: Record<string, unknown> = {};
      for (const [f] of IDENTITY) {
        const fd = draft[f];
        if (fd.value.trim() !== (record[f].value ?? '')) body[f] = fd.value.trim() || null;
        if (fd.verdict === 'clear') validation[f] = null;
        else if (fd.verdict) {
          validation[f] = { status: fd.verdict, method: fd.method, evidence: fd.evidence };
        }
      }
      if (draft.ownershipPattern !== (record.ownershipPattern?.value ?? '')) {
        body.ownershipPattern = draft.ownershipPattern || null;
      }
      if (
        draft.purposeCategory !== (record.purpose?.value.category ?? '') ||
        draft.purposeDescription !== (record.purpose?.value.description ?? '')
      ) {
        body.purpose = draft.purposeCategory
          ? { category: draft.purposeCategory, description: draft.purposeDescription || null }
          : null;
      }
      if (Object.keys(validation).length) body.validation = validation;
      if (!opts.force) body.expectedVersion = record.version;
      return (await api.patch(`/admin/subscriber-register/${teamId}`, body)).data
        .data as RegisterRecord;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(qk.subscriberRegisterRecord(teamId), saved);
      queryClient.invalidateQueries({ queryKey: qk.subscriberRegister() });
      setDirty(false);
      setConflict(null);
      setDraft(draftFrom(saved));
      toast.success('Register record saved');
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        setConflict({ serverVersion: conflictCurrentVersion(err) ?? record?.version ?? 0 });
        return;
      }
      toast.error(apiErrorMessage(err, 'Could not save the record'));
    },
  });

  return (
    <DataState query={recordQuery}>
      {(r) => (
        <div className="space-y-5 text-sm">
          <DialogHeader>
            <DialogTitle>{r.team.name ?? r.teamId}</DialogTitle>
            <DialogDescription>
              Team {r.teamId} ·{' '}
              {r.status === 'open'
                ? 'open'
                : `closed ${fmtDate(r.closedAt)} (${r.closedReason}), retained until ${fmtDate(r.retainUntil)}`}
            </DialogDescription>
          </DialogHeader>

          {conflict && (
            <div className="flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  This record changed since you opened it (server has version{' '}
                  {conflict.serverVersion}, you loaded {r.version}). Your edits are still here.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setConflict(null);
                    setDirty(false);
                    queryClient.invalidateQueries({
                      queryKey: qk.subscriberRegisterRecord(teamId),
                    });
                  }}
                >
                  Discard &amp; load latest
                </Button>
                <Button size="sm" onClick={() => save.mutate({ force: true })}>
                  Overwrite with my changes
                </Button>
              </div>
            </div>
          )}

          <section className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            <Fact label="Owner" value={`${r.owner.name ?? '—'} <${r.owner.email ?? '—'}>`} />
            <Fact label="Email verified" value={r.owner.emailVerified ? 'yes' : 'no'} />
            <Fact label="Signup" value={`${fmtDateTime(r.signup.at)} via ${r.signup.method}`} />
            <Fact label="Signup email" value={r.signup.email ?? '—'} />
            <Fact
              label="Signup IP"
              value={
                r.signup.ip ??
                (r.signup.capture === 'backfill' ? 'not captured (pre-register account)' : '—')
              }
            />
            <Fact label="User agent" value={r.signup.userAgent ?? '—'} />
            <Fact
              label="Hire period"
              value={`${fmtDate(r.period.startedAt)} → ${r.period.endedAt ? fmtDate(r.period.endedAt) : 'now'}`}
            />
            <Fact label="First paid" value={fmtDate(r.period.firstPaidAt)} />
            {r.owner.accountDeletedAt && (
              <Fact label="Owner account deleted" value={fmtDate(r.owner.accountDeletedAt)} />
            )}
            {r.billing && (
              <Fact
                label={`Billing (${r.billing.gateway})`}
                value={[
                  r.billing.name,
                  r.billing.email,
                  r.billing.phone,
                  r.billing.address,
                  ...(r.billing.taxIds ?? []),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            )}
          </section>

          <section>
            <h3 className="mb-1 font-medium">Hostnames</h3>
            <p className="text-muted-foreground mb-2 text-xs">{r.network.note}</p>
            <ul className="space-y-0.5 text-xs">
              {r.network.hostnames.length === 0 && <li>—</li>}
              {r.network.hostnames.map((h) => (
                <li key={`${h.hostname}-${h.addedAt}`}>
                  <span className="font-mono">{h.hostname}</span> · {fmtDate(h.addedAt)} →{' '}
                  {h.removedAt ? fmtDate(h.removedAt) : 'in use'}
                </li>
              ))}
            </ul>
            {r.period.paid.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs">
                {r.period.paid.map((p, i) => (
                  <li key={i}>
                    {p.plan} ({p.gateway}) · {fmtDate(p.startedAt)} →{' '}
                    {p.endedAt ? fmtDate(p.endedAt) : 'active'}
                  </li>
                ))}
              </ul>
            )}
            {r.ownerHistory.length > 0 && (
              <p className="text-muted-foreground mt-2 text-xs">
                Previous owners:{' '}
                {r.ownerHistory
                  .map((h) => `${h.email ?? '—'} (until ${fmtDate(h.until)})`)
                  .join(', ')}
              </p>
            )}
          </section>

          {draft && (
            <section className="space-y-4 border-t pt-4">
              {IDENTITY.map(([f, label]) => (
                <div key={f} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label>{label}</Label>
                    <StatusBadge status={r[f].status} />
                    {r[f].source && (
                      <span className="text-muted-foreground text-xs">from {r[f].source}</span>
                    )}
                  </div>
                  {f === 'address' ? (
                    <Textarea
                      rows={2}
                      value={draft[f].value}
                      onChange={(e) => updateField(f, { value: e.target.value })}
                    />
                  ) : (
                    <Input
                      value={draft[f].value}
                      onChange={(e) => updateField(f, { value: e.target.value })}
                    />
                  )}
                  {r[f].validation && (
                    <p className="text-muted-foreground text-xs">
                      {r[f].validation!.status} by {r[f].validation!.method} on{' '}
                      {fmtDate(r[f].validation!.at)}: {r[f].validation!.evidence}
                    </p>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <select
                      className="border-input bg-background h-8 rounded-md border px-2 text-xs"
                      value={draft[f].verdict}
                      onChange={(e) => updateField(f, { verdict: e.target.value as Verdict })}
                    >
                      <option value="">Validation: no change</option>
                      <option value="verified">Mark verified</option>
                      <option value="rejected">Mark rejected</option>
                      <option value="clear">Clear verdict</option>
                    </select>
                    {(draft[f].verdict === 'verified' || draft[f].verdict === 'rejected') && (
                      <>
                        <select
                          className="border-input bg-background h-8 rounded-md border px-2 text-xs"
                          value={draft[f].method}
                          onChange={(e) => updateField(f, { method: e.target.value })}
                        >
                          {VALIDATION_METHODS.map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                        <Input
                          className="h-8 text-xs"
                          placeholder="Evidence (document, reference, who checked)"
                          value={draft[f].evidence}
                          onChange={(e) => updateField(f, { evidence: e.target.value })}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Ownership pattern</Label>
                  <select
                    className="border-input bg-background h-9 w-full rounded-md border px-3"
                    value={draft.ownershipPattern}
                    onChange={(e) => update({ ownershipPattern: e.target.value })}
                  >
                    <option value="">Not declared</option>
                    {OWNERSHIP_PATTERNS.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Purpose of hiring</Label>
                  <select
                    className="border-input bg-background h-9 w-full rounded-md border px-3"
                    value={draft.purposeCategory}
                    onChange={(e) => update({ purposeCategory: e.target.value })}
                  >
                    <option value="">Not declared</option>
                    {PURPOSES.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Input
                placeholder="Purpose details (optional)"
                value={draft.purposeDescription}
                onChange={(e) => update({ purposeDescription: e.target.value })}
              />

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">
                  {r.gaps.length
                    ? `Missing: ${r.gaps.map((g) => GAP_LABELS[g] ?? g).join(', ')}`
                    : 'Every CERT-In item is answered.'}
                </span>
                <Button
                  size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() => save.mutate({})}
                >
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </section>
          )}
        </div>
      )}
    </DataState>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="py-0.5">
      <span className="text-muted-foreground">{label}: </span>
      <span className="break-all">{value}</span>
    </div>
  );
}
